import {_ReactPixi, applyDefaultProps, PixiComponent} from '@inlet/react-pixi';
import {Rectangle} from 'pixi.js';
import {CoreDI} from '@gongxigames/framework/core/configs';
import {FlexBox, FlexBoxOptions} from '@gongxigames/framework/core/fragments/display';
import {inversifyContainer} from '@gongxigames/framework/core/installer';

type ContainerProps = _ReactPixi.IContainer & {
    onUpdatedLayout?: (size: Rectangle) => void;
};

type FlexBoxOptionsPartial = Partial<FlexBoxOptions>;
export type FlexBoxProps = Partial<FlexBoxOptions> & ContainerProps;
type FlexBoxOptionsKeys = keyof FlexBoxOptions;

const flexBoxOptionsKeys: FlexBoxOptionsKeys[] = ['debug', 'direction', 'justifyContent', 'alignItems', 'padding', 'gap', 'wrap', 'containerWidth', 'containerHeight'];

export const FlexBoxComponent = PixiComponent<FlexBoxProps, FlexBox>('FlexBoxComponent', {
    create(props: FlexBoxProps): FlexBox {
        return new FlexBox(props, inversifyContainer.get(CoreDI.UpdateManager));
    },
    applyProps(instance: FlexBox, oldProps: FlexBoxProps, newProps: FlexBoxProps): void {
        const flexBoxOptions: Partial<FlexBoxOptions> = {};
        const containerProps: {
            old: ContainerProps;
            new: ContainerProps;
        } = {old: {}, new: {}};

        foreachContainerProps(oldProps, (key, value) => {
            setProps(containerProps.old, key, value);
        });

        foreachContainerProps(newProps, (key, value) => {
            setProps(containerProps.new, key, value);
        });

        foreachFlexBoxProps(newProps, (key, value) => {
            setProps(flexBoxOptions, key, value);
        });

        applyDefaultProps(instance, containerProps.old, containerProps.new);

        instance.updateOptions(flexBoxOptions);

        if (newProps.onUpdatedLayout || oldProps.onUpdatedLayout) {
            instance.removeAllListeners('updated');

            if (newProps.onUpdatedLayout) {
                instance.on('updated', newProps.onUpdatedLayout);
            }
        }
    }
});

const setProps = <Type, K extends keyof Type>(props: Type, key: K, value: Type[K]): void => {
    props[key] = value;
};

function foreachContainerProps<K extends keyof ContainerProps>(
    props: FlexBoxProps,
    callback: (key: K, value: ContainerProps[K]) => void): void {
    Object.keys(props).forEach(key => {
        if (!flexBoxOptionsKeys.includes(key as any)) {
            callback(key as K, props[key as K]);
        }
    });
}

function foreachFlexBoxProps<K extends keyof FlexBoxOptionsPartial>(
    props: FlexBoxProps,
    callback: (key: K, value: FlexBoxOptionsPartial[K]) => void): void {
    Object.keys(props).forEach(key => {
        if (flexBoxOptionsKeys.includes(key as any)) {
            callback(key as K, props[key as K]);
        }
    });
}
